import { useCallback, useEffect, useState } from "react";
import apiClient from "@shared/services/apiClient";

export type PortalWorkbenchCandidate = {
  studyId: string;
  patientLabel: string;
  patientFirstName?: string | null;
  patientLastName?: string | null;
  status: string;
  priority: string;
  label: string;
  confidencePercent: number | null;
  rationale: string[];
};

export type PortalWorkbenchSnapshot = {
  generatedAtIso: string;
  dataSource: "database";
  summary: {
    studies: number;
    recordings: number;
    processedRecordings: number;
    processingCompletePercent: number;
    artifacts: number;
    spikes: number;
    unresolvedBadChannels: number;
    documentTemplates: number;
    recentProcessingLogs: number;
    remoteAssistanceSessions: number;
  };
  techQc: {
    poorChannelNames: string[];
    unresolvedBadChannels: number;
    artifactEvents: number;
    processingCompletePercent: number;
    latestLogs: Array<{
      log_id: string;
      recording_id: string;
      operation_type: string;
      operation_status: string;
      started_at: string;
      completed_at: string | null;
      error_message: string | null;
      filename?: string | null;
      patient_label?: string | null;
      study_label?: string | null;
      evidence_label?: string | null;
    }>;
  };
  mobileTriage: {
    candidates: PortalWorkbenchCandidate[];
  };
  ambulatory: {
    studies: Array<{
      eeg_study_id: string;
      status: string;
      priority: string;
      study_date: string;
    }>;
    ingestLogs: Array<{
      log_id: string;
      operation_type: string;
      operation_status: string;
      ageSeconds: number | null;
    }>;
  };
  studyScreening: {
    candidates: PortalWorkbenchCandidate[];
    labeling: {
      badge: string;
      intendedUse: string;
      excludedUse: string;
    };
  };
  neonatal: {
    studies: Array<{
      eeg_study_id: string;
      status: string;
      priority: string;
      study_date: string;
      eegType?: { type_name?: string; type_code?: string };
    }>;
    templates: Array<{
      template_id: string;
      template_name: string;
      report_type: string;
      is_active: boolean;
      updated_at: string;
    }>;
  };
};

export async function fetchPortalWorkbenchSnapshot(): Promise<PortalWorkbenchSnapshot> {
  const response = await apiClient.get("/monitoring/portal-workbenches");
  return response.data?.data as PortalWorkbenchSnapshot;
}

export function usePortalWorkbenchSnapshot() {
  const [snapshot, setSnapshot] = useState<PortalWorkbenchSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSnapshot(await fetchPortalWorkbenchSnapshot());
    } catch (err: any) {
      setSnapshot(null);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Monitoring workbench data is unavailable.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { snapshot, loading, error, reload };
}
